import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Clock, CheckCircle, XCircle, Wallet, TrendUp, Buildings, Files, Hourglass,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const CHART_TICK = { fontSize: 11, fill: "#71717a", fontFamily: "IBM Plex Mono" };
const TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 };
const CAT_COLORS = ["#2563eb", "#0f766e", "#b45309", "#be123c", "#4f46e5", "#64748b", "#059669", "#7c3aed"];

const fmtInr = (n) => "₹" + Math.round(n || 0).toLocaleString("en-IN");

function Metric({ label, value, sub, tone = "default", testId }) {
  const toneMap = {
    default: "text-zinc-900", pending: "text-amber-700", approved: "text-emerald-700",
    rejected: "text-rose-700", paid: "text-blue-700", blue: "text-blue-700",
  };
  return (
    <div data-testid={testId} className="bg-white border border-zinc-200 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</div>
      <div className={`font-heading text-xl md:text-2xl font-bold tracking-tighter mt-2 tabular-nums font-mono ${toneMap[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function ColorCard({ label, count, amount, icon: Icon, bg, testId }) {
  return (
    <div data-testid={testId} className="rounded-lg p-4 text-white shadow-sm transition-transform duration-200 hover:-translate-y-0.5" style={{ backgroundColor: bg }}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-semibold leading-tight">{label}</div>
        <div className="h-8 w-8 rounded-full bg-white/20 grid place-items-center shrink-0">
          <Icon size={17} weight="duotone" />
        </div>
      </div>
      <div className="font-heading text-3xl font-bold tracking-tight mt-1 font-mono tabular-nums">
        {String(count).padStart(2, "0")}
      </div>
      <div className="text-xs font-mono opacity-95 mt-0.5">{fmtInr(amount)}</div>
    </div>
  );
}

function MonthlyChart({ data }) {
  return (
    <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-5">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Monthly expense</div>
          <div className="font-heading text-lg font-semibold text-zinc-900 mt-1">Last 6 months</div>
        </div>
        <div className="flex items-center gap-1 text-xs text-emerald-700">
          <TrendUp size={14} weight="duotone" /> Trend
        </div>
      </div>
      <div className="h-64 min-h-[16rem] min-w-0 mt-4">
        {data.length === 0 ? (
          <div className="h-full grid place-items-center text-sm text-zinc-500">No expenses yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="2 4" stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="month" tick={CHART_TICK} />
              <YAxis tick={CHART_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={fmtInr} />
              <Bar dataKey="amount" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function CategorySplit({ data }) {
  const max = Math.max(...data.map((x) => x.amount), 0);
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Category-wise</div>
      <div className="font-heading text-lg font-semibold text-zinc-900 mt-1">Spend split</div>
      <div className="mt-4 space-y-3 max-h-72 overflow-auto pr-1">
        {data.length === 0 && <div className="text-sm text-zinc-500">No category spend yet.</div>}
        {data.map((c, i) => {
          const pct = max ? (c.amount / max) * 100 : 0;
          return (
            <div key={c.category} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-700 truncate pr-2">{c.category}</span>
                <span className="font-mono text-zinc-900">{fmtInr(c.amount)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full rounded-full transition-colors duration-200" style={{ width: `${pct}%`, backgroundColor: CAT_COLORS[i % CAT_COLORS.length] }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HubChart({ data }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
        <Buildings size={14} weight="duotone" /> Hub-wise expense
      </div>
      <div className="font-heading text-lg font-semibold text-zinc-900 mt-1">By branch</div>
      <div className="mt-4 h-64 min-h-[16rem] min-w-0">
        {data.length === 0 ? (
          <div className="h-full grid place-items-center text-sm text-zinc-500">No hub data yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="2 4" stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="hub" tick={CHART_TICK} />
              <YAxis tick={CHART_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={fmtInr} />
              <Bar dataKey="amount" fill="#0f766e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function CategoryTable({ data }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Category breakdown</div>
      <div className="font-heading text-lg font-semibold text-zinc-900 mt-1">Count & amount</div>
      <div className="mt-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-200">
              <th className="py-2 font-medium">Category</th>
              <th className="py-2 font-medium text-right">Count</th>
              <th className="py-2 font-medium text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 8).map((c) => (
              <tr key={c.category} className="border-b border-zinc-100 last:border-0">
                <td className="py-2 text-zinc-800">{c.category}</td>
                <td className="py-2 text-right font-mono text-zinc-700">{c.count}</td>
                <td className="py-2 text-right font-mono text-zinc-900">{fmtInr(c.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonthlySummary({ data }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="monthly-summary-card">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Month-wise</div>
      <div className="font-heading text-lg font-semibold text-zinc-900 mt-1">Monthly summary</div>
      <div className="mt-3">
        {data.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4">No monthly data yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-200">
                <th className="py-2 font-medium">Month</th>
                <th className="py-2 font-medium text-right">Bills</th>
                <th className="py-2 pl-3 font-medium">Top hub</th>
                <th className="py-2 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 12).map((m) => (
                <tr key={m.month} data-testid={`month-row-${m.month}`} className="border-b border-zinc-100 last:border-0">
                  <td className="py-2 font-mono text-zinc-800">{m.month}</td>
                  <td className="py-2 text-right font-mono text-zinc-700">{m.count || 0}</td>
                  <td className="py-2 pl-3" data-testid={`month-tophub-${m.month}`}>
                    {m.top_hub ? (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className="text-zinc-800 font-medium">{m.top_hub}</span>
                        <span className="font-mono text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">{fmtInr(m.top_hub_amount)}</span>
                      </span>
                    ) : (
                      <span className="text-zinc-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="py-2 text-right font-mono text-zinc-900">{fmtInr(m.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/stats/overview"); setStats(data); }
      finally { setLoading(false); }
    })();
  }, []);

  const s = stats?.by_status || {};
  const pending = s.pending || { count: 0, amount: 0 };
  const correction = s.correction_requested || { count: 0, amount: 0 };
  const approved = s.approved || { count: 0, amount: 0 };
  const rejected = s.rejected || { count: 0, amount: 0 };
  const paid = s.paid || { count: 0, amount: 0 };
  const total = (pending.amount || 0) + (approved.amount || 0) + (rejected.amount || 0) + (paid.amount || 0) + (correction.amount || 0);

  const monthData = useMemo(() => (stats?.by_month || []).slice(-6), [stats]);
  const monthTable = useMemo(() => [...(stats?.by_month || [])].reverse(), [stats]);
  const catData = useMemo(() => stats?.by_category || [], [stats]);
  const hubData = useMemo(() => stats?.by_hub || [], [stats]);

  const isHubUser = user?.role === "hub_user";

  return (
    <DashboardLayout>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <ColorCard testId="metric-total" label="Total Expense Bills" bg="#2563eb" icon={Files}
          count={(pending.count || 0) + (correction.count || 0) + (approved.count || 0) + (rejected.count || 0) + (paid.count || 0)}
          amount={total} />
        <ColorCard testId="metric-pending" label="Pending Approval" bg="#f59e0b" icon={Clock}
          count={(pending.count || 0) + (correction.count || 0)}
          amount={(pending.amount || 0) + (correction.amount || 0)} />
        <ColorCard testId="metric-approved" label="Approved" bg="#0284c7" icon={CheckCircle}
          count={(approved.count || 0) + (paid.count || 0)}
          amount={(approved.amount || 0) + (paid.amount || 0)} />
        <ColorCard testId="metric-payment-pending" label="Payment Pending" bg="#8b5cf6" icon={Hourglass}
          count={approved.count || 0} amount={approved.amount || 0} />
        <ColorCard testId="metric-paid" label="Paid" bg="#16a34a" icon={Wallet}
          count={paid.count || 0} amount={paid.amount || 0} />
        <ColorCard testId="metric-rejected" label="Rejected" bg="#dc2626" icon={XCircle}
          count={rejected.count || 0} amount={rejected.amount || 0} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <Metric testId="metric-thismonth" label="Current month" value={fmtInr(stats?.this_month_amount || 0)} sub={new Date().toLocaleString("en", { month: "long", year: "numeric" })} />
        <Metric testId="metric-prevmonth" label="Previous month" value={fmtInr(stats?.prev_month_amount || 0)} sub="Approved + Paid basis" />
      </div>

      {user && ["manager", "finance", "admin"].includes(user.role) && (stats?.my_queue || 0) > 0 && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-blue-700">Action Required</div>
            <div className="font-heading text-lg font-semibold text-blue-900 mt-0.5">
              {stats.my_queue} bill{stats.my_queue === 1 ? "" : "s"} awaiting your {user.role === "finance" ? "payment" : "review"}
            </div>
          </div>
          <Link data-testid="dash-goto-approvals" to="/approvals" className="text-sm font-medium text-blue-700 hover:underline">
            Open queue →
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <MonthlyChart data={monthData} />
        <CategorySplit data={catData} />
      </div>

      {!isHubUser ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <HubChart data={hubData} />
          <CategoryTable data={catData} />
          <MonthlySummary data={monthTable} />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <MonthlySummary data={monthTable} />
        </div>
      )}

      {loading && <div className="mt-6 text-sm text-zinc-500 font-mono">Loading metrics…</div>}
    </DashboardLayout>
  );
}
